/**
 * Modal Component
 *
 * Componente base para modais do sistema.
 * Segue o padrão always-light theme para modais.
 *
 * Características:
 * - Fecha com tecla ESC
 * - NÃO fecha ao clicar fora (apenas no X ou ESC)
 * - Overlay escuro semi-transparente com backdrop blur
 * - Centralizado na tela com animação de entrada
 * - Z-index alto (z-50)
 *
 * @example
 * <Modal isOpen={isOpen} onClose={handleClose}>
 *   <ModalHeader title="Título do Modal" onClose={handleClose} />
 *   <ModalContent>
 *     <p>Conteúdo do modal</p>
 *   </ModalContent>
 *   <ModalFooter>
 *     <Button onClick={handleClose}>Cancelar</Button>
 *     <Button onClick={handleConfirm}>Confirmar</Button>
 *   </ModalFooter>
 * </Modal>
 */

import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import { useTheme } from '@/contexts/theme';
import { EDITOR_OVERLAY_Z_CLASS } from './editor-overlay-layer';

// ============================================================================
// Hook useEscapeKey - Reutilizável para fechar com ESC
// ============================================================================

/**
 * Hook para detectar pressionamento da tecla ESC
 * @param onEscape Callback a ser executado quando ESC é pressionado
 * @param enabled Se o hook está ativo (default: true)
 */
export const useEscapeKey = (onEscape: () => void, enabled: boolean = true): void => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onEscape();
      }
    },
    [onEscape]
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, enabled]);
};

// ============================================================================
// Modal Component
// ============================================================================

export interface ModalProps {
  /** Se o modal está aberto */
  isOpen: boolean;
  /** Callback para fechar o modal */
  onClose: () => void;
  /** Conteúdo do modal */
  children: React.ReactNode;
  /** Largura máxima do modal (default: max-w-lg) */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  /** Classes CSS adicionais para o container */
  className?: string;
  /** Se deve fechar ao pressionar ESC (default: true) */
  closeOnEscape?: boolean;
  /**
   * Camada de empilhamento. `'default'` = `z-[60]`, o nível de overlay do DS.
   * `'editor'` = `EDITOR_OVERLAY_Z_CLASS`, acima do painel de nó e do seletor
   * de nós do editor de workflows (`z-[201]`).
   * @default "default"
   */
  layer?: 'default' | 'editor';
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: 'max-w-full',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  maxWidth = 'lg',
  className = '',
  closeOnEscape = true,
  layer = 'default',
}) => {
  const { lightTokens } = useTheme();
  const lt = lightTokens.colors;

  // Fecha com ESC
  useEscapeKey(onClose, isOpen && closeOnEscape);

  // Previne scroll do body quando modal está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Portal pro document.body: sem isso, um ancestral com transform/filter/contain
  // (ex.: painel de Drawer, card do Kanban em drag) vira o containing block do
  // `position: fixed` e o modal abre "por dentro"/atrás do pai. Ver DS §Overlays.
  return createPortal(
    <div
      className={`fixed inset-0 ${
        layer === 'editor' ? EDITOR_OVERLAY_Z_CLASS : 'z-[60]'
      } flex items-end sm:items-center justify-center`}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
    >
      {/* ≤640: bottom-sheet full-width (mockup `.mdl` mobile: 100vw, colado embaixo,
          raio só no topo). ≥sm: card centralizado com max-width. */}
      <div
        className={`relative w-full animate-modal-in max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl sm:mx-4 sm:max-h-none sm:overflow-visible ${maxWidthClasses[maxWidth]} ${className}`}
        style={{
          backgroundColor: '#ffffff',
          boxShadow: 'var(--shadow-xl)',
          // Override CSS variables to light theme (modals are always light)
          '--theme-text': lt.text.primary,
          '--theme-muted-text': lt.text.muted,
          '--theme-input-background': lt.background.input,
          '--theme-input-text': lt.text.primary,
          '--theme-input-border': lt.border.input,
          '--theme-input-focus': lt.border.focus,
          '--theme-error-color': lt.error,
          '--theme-card-background': lt.background.card,
          '--theme-card-text': lt.text.secondary,
          '--theme-card-border': lt.border.default,
          '--theme-card-muted': lt.text.muted,
          '--theme-button-primary': lt.primary,
          '--theme-button-primary-text': lt.text.inverse,
          '--theme-button-primary-hover': lt.primaryHover,
          '--theme-button-secondary': lt.background.elevated,
          '--theme-button-secondary-text': lt.text.secondary,
          '--theme-button-secondary-hover': lt.border.strong,
          '--theme-button-ghost': 'transparent',
          '--theme-button-ghost-hover': lt.background.elevated,
          '--theme-primary': lt.primary,
          '--theme-border': lt.border.default,
          '--theme-component-background': lt.background.card,
        } as React.CSSProperties}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

Modal.displayName = 'Modal';

// ============================================================================
// ModalHeader Component
// ============================================================================

export interface ModalHeaderProps {
  /** Título do modal — caminho novo preferido (padrão DS: 18px semibold, F74).
   *  Renderiza `<h2 className="text-lg font-semibold">` internamente. */
  title?: string;
  /** Conteúdo livre do header (retrocompat; se combinado com `title`,
   *  renderiza após o título — ex.: badge, ícone extra) */
  children?: React.ReactNode;
  /** Callback para fechar o modal (exibe o botão X) */
  onClose?: () => void;
  /** Classes CSS adicionais */
  className?: string;
  /** Se deve exibir a borda inferior (default: true) */
  showBorder?: boolean;
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  children,
  onClose,
  className = '',
  showBorder = true,
}) => {
  return (
    <div
      className={`flex items-center justify-between p-4 ${showBorder ? 'border-b' : ''} ${className}`}
      style={{
        borderColor: '#e5e7eb',
        borderTopLeftRadius: 'var(--radius-2xl)',
        borderTopRightRadius: 'var(--radius-2xl)',
      }}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {title && (
          <h2 className="text-lg font-semibold truncate" style={{ color: '#1e293b' }}>
            {title}
          </h2>
        )}
        {children}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg transition-colors flex-shrink-0"
          style={{ color: '#64748b' }}
          data-testid="modal-close"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f1f5f9';
            e.currentTarget.style.color = '#1e293b';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#64748b';
          }}
        >
          <Icon name="x" className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

ModalHeader.displayName = 'ModalHeader';

// ============================================================================
// ModalContent Component
// ============================================================================

export interface ModalContentProps {
  /** Conteúdo do modal */
  children: React.ReactNode;
  /** Classes CSS adicionais */
  className?: string;
  /** Padding do conteúdo (default: true) */
  padding?: boolean;
}

export const ModalContent: React.FC<ModalContentProps> = ({
  children,
  className = '',
  padding = true,
}) => {
  return (
    <div className={`${padding ? 'p-4' : ''} ${className}`}>
      {children}
    </div>
  );
};

ModalContent.displayName = 'ModalContent';

// ============================================================================
// ModalFooter Component
// ============================================================================

export interface ModalFooterProps {
  /** Conteúdo do footer (botões) */
  children: React.ReactNode;
  /** Classes CSS adicionais */
  className?: string;
  /** Se deve exibir a borda superior (default: true) */
  showBorder?: boolean;
}

export const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  className = '',
  showBorder = true,
}) => {
  return (
    <div
      className={`flex items-center justify-end gap-3 px-4 py-3 ${showBorder ? 'border-t' : ''} ${className}`}
      style={{
        backgroundColor: '#f8fafc',
        borderColor: '#e5e7eb',
        borderBottomLeftRadius: 'var(--radius-2xl)',
        borderBottomRightRadius: 'var(--radius-2xl)',
      }}
    >
      {children}
    </div>
  );
};

ModalFooter.displayName = 'ModalFooter';

export default Modal;
